import React, { useMemo, useState } from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Paper,
  Tabs,
  Card,
  Group,
  Stack,
  Anchor,
  Table,
  ScrollArea,
  ActionIcon,
  TextInput,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const ColorLegend = ({ mode }) => {
  const legendData = mode === '3m'
    ? [
      { color: '#73A57D', label: 'Sales in all 3 months of the past 3 months' },
      { color: '#D1A752', label: 'Sales in 2 of the past 3 months' },
      { color: '#D88C8C', label: 'Sales in 1 or fewer of the past 3 months' }
    ]
    : [
      { color: '#73A57D', label: 'Sales in 83% or more of the past 12 months' },
      { color: '#D1A752', label: 'Sales in 50-82% of the past 12 months' },
      { color: '#D88C8C', label: 'Sales in less than 50% of the past 12 months' }
    ];

  return (
    <Card withBorder radius="md" style={{ width: '350px' }}>
      <Text size="sm" fw={500} mb="xs">
        Legend
      </Text>
      <Stack gap="xs">
        {legendData.map(({ color, label }) => (
          <Group key={label} gap="xs" align="flex-start">
            <Box
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: color,
                borderRadius: '2px',
                flexShrink: 0,
                marginTop: '2px',
              }}
            />
            <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
              {label}
            </Text>
          </Group>
        ))}
      </Stack>
    </Card>
  );
};

const SalesPerformanceScorecardRetentionDetail = () => {
  const { ccode, mode } = useParams();
  const [activeTab, setActiveTab] = useState(mode);
  const navigate = useNavigate();
  const { data: salesPersonName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  // State for pop-ups and search
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
  const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

  const computeDateRange = (mode) => {
    const now = new Date();
    let start;
    switch (mode) {
      case '3m': // current month (partial) plus last 2 full months
        start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case '12m': // current month (partial) plus last 11 full months
        start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        break;
      default:
        // fallback option (could also default to 3m, or another period as needed)
        start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
    }
    return {
      from: encodeURIComponent(start.toISOString()),
      to: encodeURIComponent(now.toISOString()),
    };
  };

  // Memoize the date range so it only recalculates when activeTab changes.
  const { from, to } = useMemo(() => computeDateRange(activeTab), [activeTab]);

  // Build the API endpoint using the computed date range.
  const endpoint = `${config.apiBaseUrl}/sales/scorecard/${ccode}/retentionDetails?from=${from}&to=${to}`;

  const { data, isLoading, error } = useAuthenticatedData(endpoint);

  // Formatter for currency values (no cents, dollars only)
  const formatCurrency = (value) => {
    if (value == null || value === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  /**
   * We group records by customer and pivot the soRevenue by month.
   * For each row we add an "Account Total" field.
   * Additionally, we compute a totals object (totalRow) that sums each revenue column.
   */
  const { columns, rows, filteredRows, totalRow } = useMemo(() => {
    if (!data) return { columns: [], rows: [], filteredRows: [], totalRow: {} };

    // Group data by accountNumber.
    const groups = {};
    data.forEach((item) => {
      const key = item.accountNumber;
      if (!groups[key]) {
        groups[key] = {
          id: key,
          customer: item.accountName,
          marketSegment: item.marketSegment,
          colorCategory: item.colorCategory,
          revenues: {},
        };
      }
      groups[key].revenues[item.yearMonth] =
        (groups[key].revenues[item.yearMonth] || 0) + item.soRevenue;
    });

    let computedColumns = [];
    let computedRows = [];
    let revenueKeys = []; // list of revenue month keys

    // Helper to format a Date as YYYY-MM.
    const formatYM = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      return `${year}-${month}`;
    };

    if (activeTab === '3m') {
      // For the 3-month view, we want revenue columns with YYYY-MM labels.
      const now = new Date();
      const currentYM = formatYM(now);
      const prev1YM = formatYM(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      const prev2YM = formatYM(new Date(now.getFullYear(), now.getMonth() - 2, 1));
      revenueKeys = [prev2YM, prev1YM, currentYM];

      computedColumns = {
        revenueKeys,
        customer: 'Customer',
        marketSegment: 'Market Segment',
        accountTotal: 'Account Total'
      };

      computedRows = Object.values(groups)
        .map((group) => {
          const row = {
            id: group.id,
            customer: group.customer,
            marketSegment: group.marketSegment,
            colorCategory: group.colorCategory,
            revenues: {}
          };
          let total = 0;
          revenueKeys.forEach((key) => {
            const value = group.revenues[key] || 0;
            row.revenues[key] = value;
            total += value;
          });
          row.accountTotal = total;
          return row;
        })
        .sort((a, b) => a.customer.localeCompare(b.customer));

    } else if (activeTab === '12m') {
      // For the 12-month view, we need one column per month plus the Customer and Account Total columns.
      const now = new Date();
      const months = [];
      const startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      for (let i = 0; i < 12; i++) {
        const d = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
        months.push(formatYM(d));
      }
      revenueKeys = months;

      computedColumns = {
        revenueKeys,
        customer: 'Customer',
        marketSegment: 'Market Segment',
        accountTotal: 'Account Total'
      };

      computedRows = Object.values(groups).map((group) => {
        const row = {
          id: group.id,
          customer: group.customer,
          marketSegment: group.marketSegment,
          colorCategory: group.colorCategory,
          revenues: {}
        };
        let total = 0;
        months.forEach((m) => {
          const value = group.revenues[m] || 0;
          row.revenues[m] = value;
          total += value;
        });
        row.accountTotal = total;
        return row;
      });
    }

    // Filter rows based on search term
    const filteredRows = computedRows.filter(row =>
      !searchTerm ||
      row.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.marketSegment.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Compute totals across all rows.
    const totals = revenueKeys.reduce((acc, key) => {
      acc[key] = computedRows.reduce((sum, row) => sum + (row.revenues[key] || 0), 0);
      return acc;
    }, {});
    totals.accountTotal = computedRows.reduce(
      (sum, row) => sum + (row.accountTotal || 0),
      0
    );

    // Use an object for totalRow that matches the column fields.
    const totalRow = { ...totals, id: 'total', customer: 'Month Total' };

    return { columns: computedColumns, rows: computedRows, filteredRows, totalRow };
  }, [data, activeTab, searchTerm]);

  const handleTabChange = (value) => {
    navigate(`/sales/salesperson/${ccode}/scorecard/retention/${value}`);
    setActiveTab(value);
  };

  const getRowColor = (colorCategory) => {
    switch (colorCategory?.toLowerCase()) {
      case 'red': return '#D88C8C';
      case 'green': return '#73A57D';
      case 'yellow': return '#D1A752';
      default: return undefined;
    }
  };

  const exportToCSV = () => {
    if (!filteredRows.length || !columns.revenueKeys) return;

    const headers = ['Customer', 'Market Segment', ...columns.revenueKeys, 'Account Total'];
    const csvContent = [
      headers.join(','),
      ...filteredRows.map(row => [
        `"${row.customer}"`,
        `"${row.marketSegment}"`,
        ...columns.revenueKeys.map(key => row.revenues[key] || 0),
        row.accountTotal
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'retention-details.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (isLoading || isLoadingName) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        {error.message}
      </Alert>
    );
  }

  return (
    <Paper shadow="md" radius="md"  >
      <Stack gap="xs">
        {/* Header Section with Title and Helper Links */}
        <Box>
          <Text
            component="h1"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#FDB813',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              marginBottom: 0,
              textAlign: 'left'
            }}
          >
            Retention Detail - {salesPersonName}
          </Text>

          {/* Technical options: small links */}
          <Group gap="xs" mb="sm">

            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-retention-3-month',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Summary Query
            </Anchor>
            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-retention-details-3m',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Details Query
            </Anchor>
          </Group>
        </Box>

        {/* Info Cards - tighter spacing */}
        <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
          <Box style={{ flex: 1, minWidth: '300px' }}>
            <Card withBorder radius="md" style={{ height: '100%' }}>
              <Text size="sm" fw={500} mb="xs">
                About the Data
              </Text>
              <Text size="xs" c="dimmed">
                These are all of the accounts assigned to {salesPersonName} and all additional accounts they sold to, regardless of to whom they may have been assigned.
                Only the sales made by {salesPersonName} to these accounts are counted here - if someone else sold to these accounts, you won't see that here.
                On the top-level view, the percentage there is the percent of assigned accounts that {salesPersonName} has sold to in the past 3 or 12 months. Even selling $1
                will count as a sale, but only if it's made by {salesPersonName}.
              </Text>
            </Card>
          </Box>
          <ColorLegend mode={activeTab} />
        </Group>

        {/* Tabs - compact */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          styles={(theme) => ({
            tab: {
              color: theme.colorScheme === 'dark' ? '#b3b3b3' : '#666666',
              '&[data-active]': {
                color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 'bold',
              },
              padding: '6px 12px',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="3m">3 Months</Tabs.Tab>
            <Tabs.Tab value="12m">12 Months</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {/* Toolbar - reduced spacing */}
        <Group justify="space-between" mb="xs">
          <TextInput
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftSection={<IconSearch size={16} />}
            style={{ width: '300px' }}
            size="sm"
          />
          <Group gap="xs">
            <ActionIcon
              variant="default"
              onClick={exportToCSV}
              title="Export to CSV"
              size="sm"
            >
              <IconDownload size={16} />
            </ActionIcon>
            <ActionIcon
              variant="default"
              onClick={() => window.print()}
              title="Print"
              size="sm"
            >
              <IconPrinter size={16} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Data Table - compact height */}
        <Paper withBorder style={{ height: '500px' }}>
          <ScrollArea style={{ height: '100%' }}>
            <Table striped highlightOnHover size="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '200px' }}>Customer</Table.Th>
                  <Table.Th style={{ width: '150px' }}>Market Segment</Table.Th>
                  {columns.revenueKeys?.map(key => (
                    <Table.Th key={key} style={{ width: '120px' }}>
                      {key}
                    </Table.Th>
                  ))}
                  <Table.Th style={{ width: '130px' }}>Account Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredRows.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{
                      backgroundColor: getRowColor(row.colorCategory),
                    }}
                  >
                    <Table.Td>
                      <Text size="xs" fw={500} c="dark">
                        {row.customer}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dark">
                        {row.marketSegment}
                      </Text>
                    </Table.Td>
                    {columns.revenueKeys?.map(key => (
                      <Table.Td key={key}>
                        <Text size="xs" fw={600} c="dark">
                          {formatCurrency(row.revenues[key])}
                        </Text>
                      </Table.Td>
                    ))}
                    <Table.Td>
                      <Text size="xs" fw={600} c="dark">
                        {formatCurrency(row.accountTotal)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}

                {/* Totals Row */}
                <Table.Tr style={{ backgroundColor: '#e0e0e0', fontWeight: 'bold' }}>
                  <Table.Td>
                    <Text size="xs" fw={700} c="dark">
                      {totalRow.customer}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dark"></Text>
                  </Table.Td>
                  {columns.revenueKeys?.map(key => (
                    <Table.Td key={key}>
                      <Text size="xs" fw={700} c="dark">
                        {formatCurrency(totalRow[key])}
                      </Text>
                    </Table.Td>
                  ))}
                  <Table.Td>
                    <Text size="xs" fw={700} c="dark">
                      {formatCurrency(totalRow.accountTotal)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>

        {/* Results count - minimal spacing */}
        <Text size="xs" c="dimmed">
          Showing {filteredRows.length} result{filteredRows.length !== 1 ? 's' : ''}
        </Text>
      </Stack>

      {/* Pop-up dialogs */}
      <MembershipPopup open={openMembership} onClose={handleCloseMembership} />

      <SQLEditorPopup
        open={openSummaryQuery}
        onClose={handleCloseSummaryQuery}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/retention-summary`}
      />

      <SQLEditorPopup
        open={openDetailsQuery}
        onClose={handleCloseDetailsQuery}
        title="Details Query"
        endpoint={`${config.apiBaseUrl}/sales/scorecard/queries/retention-details`}
      />
    </Paper>
  );
};

export default SalesPerformanceScorecardRetentionDetail;